
import React from 'react';
import { ChevronRight } from 'lucide-react';
import { SiteConfig } from '../types';

interface HeroProps {
  config: SiteConfig;
  onContactClick: () => void;
}

// Spigot Icon SVG Component - Updated to allow color overriding via className
export const SpigotIcon = ({ className = "w-5 h-5 fill-current" }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className={className}>
    <path fill="currentColor" d="M12.644 2.44a1.4 1.4 0 0 0-.603.331c-.245.2-.588.232-2.023.133l-1.713-.116l.049.713l.049.713h.652c.36-.016 1.207-.05 1.876-.083l1.224-.083v3.317l-.44.05c-.425.05-.457.1-.457.862c0 .713-.05.813-.36.863c-.26.033-.39.182-.44.464c-.016.232-.114.448-.18.497c-.08.05-.228.597-.326 1.211c-.228 1.526-.375 1.708-1.37 1.84c-1.436.167-2.056.134-2.056-.148c0-.2-.244-.25-1.158-.25c-1.012 0-1.158-.032-1.24-.33c-.065-.25-.228-.333-.62-.333s-.555.083-.62.332c-.082.299-.228.332-1.224.332c-1.011 0-1.158.033-1.256.332c-.049.182-.18.331-.26.331c-.082 0-.148.863-.148 1.99c0 1.609.05 1.99.229 1.99c.13 0 .293.15.342.332c.082.282.245.332 1.175.332c.914 0 1.077.05 1.142.331c.13.465 1.11.465 1.24 0c.065-.282.228-.331 1.158-.331c.849 0 1.077-.05 1.077-.25c0-.397 2.121-.33 3.426.117c1.583.53 5.14.53 6.82 0c.653-.199 1.256-.332 1.338-.282c.359.232.163.896-.343 1.178c-.587.298-.587.563 0 1.956l.343.797l1.599-.067c1.73-.083 2.822-.48 3.915-1.41l.539-.464l-.31-.912c-.327-.962-.734-1.327-1.518-1.327c-.342 0-.473-.149-.766-.796c-.506-1.144-1.224-1.758-2.758-2.355c-.799-.315-1.582-.746-1.99-1.127c-.604-.548-.685-.73-.832-1.775c-.098-.63-.245-1.194-.326-1.244c-.066-.05-.164-.265-.18-.497c-.049-.282-.18-.431-.424-.464c-.326-.05-.375-.15-.375-.863c0-.763-.033-.812-.44-.862c-.458-.05-.458-.05-.507-1.526c-.032-.929.017-1.542.13-1.658c.115-.116.93-.183 2.09-.183h1.908l.05-.564c.032-.298-.017-.63-.099-.713c-.098-.1-.816-.083-1.909.05c-1.256.15-1.778.15-1.86.017c-.146-.25-.848-.481-1.24-.398"/>
  </svg>
);

export const Hero: React.FC<HeroProps> = ({ config }) => {
  return (
    <section id="home" className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
      {/* Background Grid - SVG Based for cleaner rendering */}
      <div className="absolute inset-0 opacity-[0.15] pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#3b82f6" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
        </svg>
        {/* Mask to fade edges */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,#020617_80%)]"></div>
      </div>

      {/* Simple Glow Orb */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-mc-blue/10 rounded-full blur-[100px] -z-10 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Text Content */}
          <div className="space-y-8 animate-in fade-in slide-in-from-left-8 duration-1000">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-mc-blue/10 border border-mc-blue/20 text-mc-blue text-xs font-mono">
              <span className="w-2 h-2 rounded-full bg-mc-blue animate-pulse"></span>
              Available for Commission
            </div>
            
            <h1 className="text-5xl md:text-6xl font-extrabold text-white leading-tight tracking-tight">
              Bringing Your <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-mc-blue to-cyan-400">
                Server Ideas
              </span> <br/>
              To Life
            </h1>
            
            <div className="text-lg text-slate-400 max-w-xl leading-relaxed space-y-4">
              <p>
                Hi, I'm Zivush. I create fully configurable, high-quality Minecraft plugins.
                My focus is on delivering polished experiences, from complex minigames to custom abilities.
              </p>
              <p>
                Everything is built with customizability and performance in mind, ensuring your server stands out.
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <a 
                href="#portfolio" 
                className="group px-6 py-3 bg-mc-blue text-slate-950 font-bold rounded-lg flex items-center gap-2 hover:bg-blue-400 transition-all shadow-[0_4px_0_#1e40af] active:shadow-none active:translate-y-[4px]"
              >
                View Projects
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              
              <a 
                href={config.spigotUrl || '#'} 
                target="_blank" 
                rel="noreferrer" 
                className="px-6 py-3 bg-yellow-400 text-slate-900 font-bold rounded-lg flex items-center gap-2 hover:bg-yellow-300 transition-all shadow-[0_4px_0_#ca8a04] active:shadow-none active:translate-y-[4px]"
              >
                <SpigotIcon className="w-5 h-5 text-yellow-900 fill-current" />
                <span>SpigotMC Plugins</span>
              </a>
            </div>
          </div>

          {/* Code Visual - Fixed double rectangle bug by using shadow instead of separate div */}
          <div className="relative hidden lg:block animate-float">
            <div className="relative bg-slate-950 border border-slate-800 rounded-xl p-6 font-mono text-sm shadow-[0_0_50px_-12px_rgba(59,130,246,0.3)]">
              {/* Window Controls */}
              <div className="flex items-center gap-2 mb-4 border-b border-slate-800 pb-4">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="ml-2 text-slate-500 text-xs">CorePlugin.java</span>
              </div>
              
              {/* Code Content */}
              <div className="space-y-1 text-slate-300">
                <p><span className="text-purple-400">public class</span> <span className="text-yellow-300">CorePlugin</span> <span className="text-purple-400">extends</span> <span className="text-yellow-300">JavaPlugin</span> {'{'}</p>
                <p className="pl-4"><span className="text-purple-400">@Override</span></p>
                <p className="pl-4"><span className="text-purple-400">public void</span> <span className="text-blue-400">onEnable</span>() {'{'}</p>
                <p className="pl-8"><span className="text-slate-500">// Initialize modules</span></p>
                <p className="pl-8">getLogger().info(<span className="text-green-400">"Systems Online."</span>);</p>
                <p className="pl-8"><span className="text-yellow-300">GameManager</span>.loadArenas();</p>
                <p className="pl-8"><span className="text-yellow-300">DatabaseManager</span>.connect();</p>
                <p className="pl-4">{'}'}</p>
                <p className="pl-4"></p>
                <p className="pl-4"><span className="text-purple-400">public void</span> <span className="text-blue-400">handleEvent</span>(<span className="text-yellow-300">PlayerInteractEvent</span> e) {'{'}</p>
                <p className="pl-8"><span className="text-slate-500">// Custom mechanics logic</span></p>
                <p className="pl-8"><span className="text-purple-400">if</span> (e.getItem() == <span className="text-purple-400">null</span>) <span className="text-purple-400">return</span>;</p>
                <p className="pl-4">{'}'}</p>
                <p>{'}'}</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
